import React from "react";

import { captureMessage } from "@sentry/core";
import { AllSelection, NodeSelection, Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { v4 } from "uuid";

import { BlockSelection } from "~/lib/prose/blockselection";
import { MenuExtension } from "~/lib/prose/extension";
import { reactRender } from "~/lib/prose/plugins/reactHost";
import { MEETING_CARD_CONTENT_ELEMENT_ID } from "~/meetings/utils";

import FloatingToolbar from "./FloatingToolbar";

import styles from "./FloatingToolbar.module.css";

const PLUGIN_NAME = "formatting-tools";

export class FloatingFormattingTools extends MenuExtension {
	/**
	 * When the selection changes during the toolbar's entry or exit animations, if there is only one instance of the
	 * toolbar, it moves to the new position in a very noticeable way that I don't like.
	 *
	 * To account for this, every time a new selection is started, a new instance of the toolbar is created (and fades in).
	 * Conversely, when a selection is cleared, the toolbar is immediately unmounted (and fades out).
	 *
	 * Only when the selection is updated does an already visible toolbar animate from one position to the other.
	 *
	 * Since it's possible to select -> unselect -> select before the first toolbar fades out, we need to be able to have
	 * multiple instances of the toolbar exist at once.
	 *
	 * Rest assured, as long as the animations are quick, there's practically only ever one toolbar visible on screen, and
	 * therefore only one toolbar in this array.
	 */
	private renderedToolbars: HTMLDivElement[];

	get toolbarDOM() {
		return this.renderedToolbars.at(-1);
	}

	get name() {
		return PLUGIN_NAME;
	}

	constructor() {
		super();
		this.renderedToolbars = new Array<HTMLDivElement>();
	}

	get plugins() {
		const extension = this;
		return [
			new Plugin({
				key: new PluginKey(PLUGIN_NAME),
				view: () => {
					return {
						update: (view: EditorView, prevState) => {
							if (!view.editable) {
								extension.unmountToolbar();
								return;
							}

							const selectionExists = view.state.selection.from !== view.state.selection.to;
							const selectionExistedInPrevState = prevState.selection.from !== prevState.selection.to;

							if (selectionExists) {
								if (selectionExistedInPrevState) {
									extension.updateToolbarPosition();
								} else {
									extension.mountToolbar();

									// The `updateToolbarPosition` function relies on the size of the toolbar to determine whether it's
									// offscreen. On the first render though, that size is 0, so it can appear offscreen until the next
									// render.
									// This timeout repositions it soon after it is rendered to make sure it's not offscreen.
									setTimeout(extension.updateToolbarPosition.bind(extension), 150);
								}
							} else {
								extension.unmountToolbar();
							}
						},
						destroy() {
							extension.unmountToolbar();
						},
					};
				},
				props: {
					handleDOMEvents: {
						focus() {
							extension.toolbarDOM?.setAttribute("data-visible", "true");
							return false;
						},
						blur() {
							extension.toolbarDOM?.setAttribute("data-visible", "false");
							return false;
						},
					},
				},
			}),
		];
	}

	get portalContainer() {
		return (
			this.editorView?.dom.closest<HTMLElement>("[data-reach-dialog-overlay]") ??
			this.editorView?.dom.closest<HTMLElement>("#root") ??
			document.body
		);
	}

	updateToolbarPosition = () =>
		requestAnimationFrame(() => {
			const { editorView: view } = this;
			if (!view) return;

			const parentElement = view.dom.parentElement;
			if (!parentElement) return;

			const parentBox = parentElement.getBoundingClientRect();

			const meetingCard = document.getElementById(MEETING_CARD_CONTENT_ELEMENT_ID);
			if (!meetingCard) return;

			const meetingCardBox = meetingCard.getBoundingClientRect();

			const { selection } = view.state;

			// We're not interested in node selections (like embeds)
			if (selection instanceof NodeSelection) {
				return;
			}

			// Only update the position of the most recently added toolbar. Other toolbars should be in the middle of fading out.
			const toolbar = this.toolbarDOM;
			if (!toolbar) return;

			// The toolbar is positioned against `portalContainer`, and we'll need to account for that in a moment
			const portalContainerRect = this.portalContainer.getBoundingClientRect();
			const portalContainerXOffset = portalContainerRect.left;
			const portalContainerYOffset = portalContainerRect.top;

			let viewportXPosition: number | null = null;
			let viewportYPosition: number | null = null;

			if (selection instanceof TextSelection) {
				const { from, to } = selection;

				// Get the viewport coordinates for the start and end of the selection
				const fromPos = view.coordsAtPos(from);
				const toPos = view.coordsAtPos(to);

				// Calculate the horizontal midpoint of the selection
				viewportXPosition = (fromPos.left + toPos.left) / 2;

				// Grab the top-most point of the selection
				viewportYPosition = fromPos.top;
			} else if (selection instanceof BlockSelection) {
				const { from } = selection;

				// Blocks always span the width of the note, so we know that its x value is 50%.
				// All that's remaining is to find the y value.
				viewportYPosition = view.coordsAtPos(from).top;

				// Find the horizontal mid-point of the editor
				viewportXPosition = parentBox.left + parentBox.width / 2;
			} else if (selection instanceof AllSelection) {
				// Everything is selected, so we can safely position the toolbar at the top
				viewportYPosition = view.coordsAtPos(0).top;

				// Find the horizontal mid-point of the editor
				viewportXPosition = parentBox.left + parentBox.width / 2;
			}

			// I'm 100% sure this can't happen, but I thought of it so I'm accounting for it anyway.
			if (viewportXPosition === null || viewportYPosition === null) {
				const message = "Unhandled selection type";
				console.warn(message, selection);
				captureMessage(message, {
					level: "warning",
					extra: {
						selection,
					},
				});

				return;
			}

			// Prevent the toolbar from being offscreen. `meetingCardBox` is always the scrolling container, i.e. where the
			// content of the note gets cut off.
			const toolbarRect = toolbar.getBoundingClientRect();
			const clampedYPosition = Math.max(
				meetingCardBox.top + 8,
				Math.min(viewportYPosition, meetingCardBox.top + meetingCardBox.height),
			);
			const clampedXPosition = Math.max(
				toolbarRect.width / 2 + 8, // 8px is a padding
				Math.min(viewportXPosition, window.innerWidth - toolbarRect.width / 2 - 8),
			);

			// If the toolbar is too far offscreen, hide it.
			if (Math.abs(clampedYPosition - viewportYPosition) > toolbarRect.height * 2) {
				toolbar.setAttribute("data-visible", "false");
			} else {
				toolbar.setAttribute("data-visible", "true");
			}

			// The coordinates we have so far are relative to the whole screen, the toolbar is positioned against the
			// portalContainer, so we need to subtract the portalContainer's offset from the coordinates.
			// NOTE: We also use `translate` in CSS to ensure the toolbar is positioned above the selection and not on top of it.
			const relativeYPosition = clampedYPosition - portalContainerYOffset;
			const relativeXPosition = clampedXPosition - portalContainerXOffset;

			toolbar.style.top = `${relativeYPosition}px`;
			toolbar.style.left = `${relativeXPosition}px`;

			this.renderToolbar(toolbar);
		});

	mountToolbar = () => {
		if (!this.editorView) return;

		const toolbar = document.createElement("div");

		toolbar.classList.add(styles.toolbarWrapper);
		toolbar.setAttribute("portal-id", v4());
		toolbar.setAttribute("data-visible", "false");
		this.renderedToolbars.push(toolbar);

		this.portalContainer.appendChild(toolbar);

		this.updateToolbarPosition();

		/**
		 * `reactRender` has a 100ms debounce for updating prose's ReactHost plugin, and it gets in the way of doing entry
		 * animations (i.e. with @starting-style).
		 * This timeout waits for any delays to pass before doing the fade in animation
		 */
		setTimeout(() => {
			toolbar.setAttribute("data-visible", "true");
		}, 100);
	};

	renderToolbar = (toolbar: HTMLDivElement) => {
		if (!this.editorView) return;

		const isNarrowWidth = this.portalContainer.getBoundingClientRect().width < 600;

		reactRender(
			this.editorView,
			toolbar,
			<FloatingToolbar
				editorView={this.editorView}
				entries={this.allEntries}
				extension={this}
				isNarrowWidth={isNarrowWidth}
			/>,
			true,
		);
	};

	unmountToolbar = () => {
		// Unmount the oldest toolbar in the array, which is the first one.
		const toolbar = this.renderedToolbars.shift();
		if (!toolbar) return;

		toolbar.setAttribute("data-visible", "false");

		setTimeout(() => {
			if (!this.editorView) return;

			reactRender(this.editorView, toolbar, null, true);
			toolbar.remove();
		}, 200); // The duration of the fade out animation
	};

	init = () => {
		const meetingCard = document.getElementById(MEETING_CARD_CONTENT_ELEMENT_ID);
		if (meetingCard) {
			meetingCard.addEventListener("scroll", this.updateToolbarPosition);
		}
	};

	destroy = () => {
		const meetingCard = document.getElementById(MEETING_CARD_CONTENT_ELEMENT_ID);
		if (meetingCard) {
			meetingCard.removeEventListener("scroll", this.updateToolbarPosition);
		}
		this.renderedToolbars.forEach(toolbar => {
			if (this.editorView) {
				reactRender(this.editorView, toolbar, null, true);
			}
			toolbar.remove();
		});
		this.renderedToolbars = new Array<HTMLDivElement>();
	};
}
